import { useCallback, useEffect, useRef, useState } from "react";
import { DEMO_MODE, DEVICE_ID, supabase } from "../supabaseClient";
import {
  type CarState,
  type Command,
  type CommandType,
  type WindowId,
  INITIAL_STATE,
  WINDOWS,
} from "../types";

export interface Toast {
  id: number;
  text: string;
  kind: "ok" | "warn" | "err";
}

// Apply a command to a local car state — the demo simulator AND the optimistic UI both use it.
function applyCommand(s: CarState, type: CommandType, target?: WindowId | "all" | null): CarState {
  const n: CarState = { ...s, windows: { ...s.windows }, lastSeen: Date.now(), online: true };
  const wins = (): WindowId[] => (target === "all" || !target ? WINDOWS : [target as WindowId]);
  switch (type) {
    case "lock": n.locked = true; break;
    case "unlock": n.locked = false; break;
    case "win_up": for (const w of wins()) n.windows[w] = 0; break;
    case "win_down": for (const w of wins()) n.windows[w] = 100; break;
    case "win_vent": for (const w of wins()) n.windows[w] = 20; break;
    case "boot": n.bootOpen = !n.bootOpen; break;
    case "ac_on": n.ac = true; if (n.blower === 0) n.blower = 2; break;
    case "ac_off": n.ac = false; n.blower = 0; break;
    case "kill_arm": n.killArmed = true; break;
    case "kill_disarm": n.killArmed = false; break;
    case "reserve_arm": n.reserveArmed = true; break;
    case "start":
      n.engineRunning = true; n.ignition = true; n.battV = 13.9;
      break;
    case "stop":
      n.engineRunning = false; n.ignition = false; n.battV = 12.5; n.ac = false; n.blower = 0;
      break;
    case "abort":
      // drop everything to a safe idle
      n.engineRunning = false; n.ignition = false; n.ac = false; n.blower = 0;
      n.battV = 12.5;
      break;
    case "scene_precool":
      n.engineRunning = true; n.ignition = true; n.battV = 13.9; n.ac = true; n.blower = 4;
      for (const w of ["fl", "fr"] as WindowId[]) n.windows[w] = 20;
      break;
    case "scene_leave":
      n.locked = false; n.ac = true; if (n.blower === 0) n.blower = 2;
      break;
    case "scene_park":
      n.locked = true; for (const w of WINDOWS) n.windows[w] = 0; n.killArmed = true;
      break;
    default:
      if (type.startsWith("blower_")) {
        const lvl = Number(type.split("_")[1]) as 0 | 1 | 2 | 3 | 4;
        n.blower = lvl; if (lvl > 0) n.ac = true; else n.ac = false;
      }
  }
  return n;
}

// Precondition guard — mirrors firmware rules (§9). Returns a rejection reason or null.
function reject(s: CarState, type: CommandType): string | null {
  if (type === "boot" && s.locked) return "Boot works only when the car is unlocked";
  if ((type === "ac_on" || type.startsWith("blower_")) && type !== "blower_0" && !s.engineRunning)
    return "Engine not running — AC would blow hot air";
  if (type === "start") {
    if (s.engineRunning) return "Engine already running";
    if (!s.neutral) return "Not in neutral — crank blocked";
    if (!s.handbrake) return "Handbrake off — crank blocked";
    if (Object.values(s.doors).some(Boolean)) return "Door open — crank blocked";
    if (!s.locked && s.bootOpen) return "Secure the car first";
    if (s.killArmed) return "Kill switch armed — disarm first";
    if (!s.reserveArmed) return "Remote-start not reserved";
  }
  return null;
}

let toastSeq = 1;

export function useCarState() {
  const [state, setState] = useState<CarState>(() => {
    // ?state=show (dev): start with everything animated — windows down, boot open, engine running
    if (new URLSearchParams(location.search).get("state") === "show")
      return { ...INITIAL_STATE, locked: false, bootOpen: true, engineRunning: true, ignition: true, ac: true, blower: 3, battV: 13.9, windows: { fl: 100, fr: 100, rl: 100, rr: 20 }, doors: { fl: true, fr: false, rl: false, rr: false } };
    return INITIAL_STATE;
  });
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [busy, setBusy] = useState<CommandType | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const toast = useCallback((text: string, kind: Toast["kind"] = "ok") => {
    const id = toastSeq++;
    setToasts((t) => [...t, { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200);
  }, []);

  // Live mode: subscribe to telemetry rows for this device.
  useEffect(() => {
    const client = supabase;
    if (DEMO_MODE || !client) return;
    const ch = client
      .channel("telemetry")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "telemetry" },
        (payload) => {
          const r = payload.new as Record<string, unknown>;
          setState((s) => ({
            ...s,
            online: true,
            lastSeen: Date.now(),
            battV: (r.batt_v as number) ?? s.battV,
            cabinC: (r.cabin_c as number) ?? s.cabinC,
            ignition: (r.ignition as boolean) ?? s.ignition,
            engineRunning: (r.engine_running as boolean) ?? s.engineRunning,
            locked: (r.doors_locked as boolean) ?? s.locked,
            neutral: (r.neutral as boolean) ?? s.neutral,
            handbrake: (r.handbrake as boolean) ?? s.handbrake,
            killArmed: (r.kill_armed as boolean) ?? s.killArmed,
            reserveArmed: (r.reserve_armed as boolean) ?? s.reserveArmed,
          }));
        }
      )
      .subscribe();
    return () => { client.removeChannel(ch); };
  }, []);

  const send = useCallback(
    async (type: CommandType, target?: WindowId | "all" | null) => {
      const s = stateRef.current;
      if (type !== "abort") {
        const why = reject(s, type);
        if (why) { toast(why, "warn"); return; }
      }
      setBusy(type);

      if (DEMO_MODE || !supabase) {
        // Simulate an in-car round-trip.
        await new Promise((r) => setTimeout(r, type === "abort" ? 120 : 420));
        setState((cur) => applyCommand(cur, type, target));
        toast(type === "abort" ? "ABORT — all relays dropped" : `${type}${target ? " · " + target : ""} done`,
          type === "abort" ? "err" : "ok");
        setBusy(null);
        return;
      }

      const cmd: Command = { device_id: DEVICE_ID, type, target: target ?? null, status: "pending" };
      const { error } = await supabase.from("commands").insert(cmd);
      if (error) toast(`Queue failed: ${error.message}`, "err");
      else {
        // optimistic local echo; real state arrives via telemetry
        setState((cur) => applyCommand(cur, type, target));
        toast(`${type} queued`, "ok");
      }
      setBusy(null);
    },
    [toast]
  );

  const setDemoInput = useCallback((patch: Partial<CarState>) => {
    setState((s) => ({ ...s, ...patch }));
  }, []);

  return { state, send, busy, toasts, setDemoInput };
}
